import React, {useState} from "react";
import {createPortal} from "react-dom";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import toast from "react-hot-toast";
import {ArrowLeft, Headphones, Minus, Plus, ShieldCheck, ShoppingBag, ShoppingCart, Trash2} from "lucide-react";
import Button from "../Common/Button";
import useAuthStore from "../../stores/authStore";
import {ListEmpty, Modal, ModalFooter, mobileListClass, mobileRowClass, thClass} from "../../stock/StockUI";
import {apiErrorMessage, effectivePrice, formatMoney, placeMarketplaceOrder} from "./marketplaceApi";
import {PriceCell, ProductThumb} from "./MarketplaceUI";

const CHECKOUT_FORM_ID = "marketplace-checkout-form";

// Same field look as the catalogue forms
const inputClass =
    "w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm sm:text-base border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-60";
const labelClass = "block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1";

// Title bar of the two panes (same height so their hairlines line up)
const paneBarClass = "h-14 flex items-center justify-between gap-3 px-4 sm:px-6 border-b border-gray-200 dark:border-gray-700";
const paneTitleClass = "text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider";

const QuantityStepper = ({value, onChange}) => (
    <div className="inline-flex items-center rounded-xl border border-gray-200 dark:border-gray-700">
        <Button variant="ghost" size="sm" icon={Minus} title="Diminuer" onClick={() => onChange(value - 1)}/>
        <span className="w-8 text-center text-sm font-medium text-gray-900 dark:text-gray-100">{value}</span>
        <Button variant="ghost" size="sm" icon={Plus} title="Augmenter" onClick={() => onChange(value + 1)}/>
    </div>
);

const Totals = ({cart}) => (
    <div className="space-y-1 text-sm">
        <div className="flex justify-between text-gray-600 dark:text-gray-300">
            <span>Sous-total</span><span>{formatMoney(cart.subtotal)}</span>
        </div>
        {cart.discount > 0 && (
            <div className="flex justify-between text-red-600 dark:text-red-400">
                <span>Remises</span><span>-{formatMoney(cart.discount)}</span>
            </div>
        )}
        <div className="flex justify-between text-gray-600 dark:text-gray-300">
            <span>Livraison</span><span>{formatMoney(cart.deliveryFee)}</span>
        </div>
        <div className="flex justify-between items-baseline pt-3 mt-2 border-t border-gray-200 dark:border-gray-700 text-base font-semibold text-gray-900 dark:text-gray-100">
            <span>Total TTC</span><span className="text-xl">{formatMoney(cart.total)}</span>
        </div>
    </div>
);

const CheckoutModal = ({cart, onClose, onOrdered}) => {
    const account = useAuthStore((state) => state.selectedAccount);
    const [form, setForm] = useState({
        contact_name: account?.name || "",
        phone: account?.phone || "",
        address: account?.address || "",
        note: "",
    });
    const set = (key) => (e) => setForm((f) => ({...f, [key]: e.target.value}));
    // One id per checkout: a retried submit returns the same order instead of a duplicate.
    const [orderId] = useState(() => crypto.randomUUID());
    const queryClient = useQueryClient();

    const order = useMutation({
        mutationFn: () => placeMarketplaceOrder({
            id: orderId,
            ...form,
            items: cart.lines.map(({product, quantity}) => ({
                product_id: product.id,
                name: product.name,
                quantity,
                unit_price: effectivePrice(product),
                list_price: product.price,
            })),
        }),
        onSuccess: (created) => {
            toast.success(`Commande ${created.reference} envoyée, notre équipe vous recontacte rapidement`);
            queryClient.invalidateQueries({queryKey: ["marketplace", "orders"]});
            onOrdered(created);
        },
        onError: (error) => toast.error(apiErrorMessage(error, "Impossible d'envoyer la commande")),
    });

    const submit = (e) => {
        e.preventDefault();
        order.mutate();
    };

    return createPortal(
        <Modal
            title="Commander"
            subtitle={`${cart.count} article(s) · ${formatMoney(cart.total)}`}
            onClose={onClose}
            footer={<ModalFooter onCancel={onClose} label="Confirmer la commande" saving={order.isPending} form={CHECKOUT_FORM_ID}/>}
        >
            <form id={CHECKOUT_FORM_ID} onSubmit={submit} className="space-y-4">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700">
                    {cart.lines.map(({product, quantity}) => (
                        <div key={product.id} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                            <span className="text-gray-900 dark:text-gray-100">{quantity} × {product.name}</span>
                            <span className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                {formatMoney(effectivePrice(product) * quantity)}
                            </span>
                        </div>
                    ))}
                    <div className="px-3 py-2"><Totals cart={cart}/></div>
                </div>

                <div>
                    <label className={labelClass} htmlFor="mk-contact">Nom du contact *</label>
                    <input id="mk-contact" required className={inputClass} value={form.contact_name} onChange={set("contact_name")}/>
                </div>
                <div>
                    <label className={labelClass} htmlFor="mk-phone">Téléphone *</label>
                    <input id="mk-phone" type="tel" required className={inputClass} value={form.phone} onChange={set("phone")}/>
                </div>
                <div>
                    <label className={labelClass} htmlFor="mk-address">Adresse de livraison *</label>
                    <textarea id="mk-address" required rows={2} className={inputClass} value={form.address} onChange={set("address")}/>
                </div>
                <div>
                    <label className={labelClass} htmlFor="mk-note">Remarque</label>
                    <textarea id="mk-note" rows={2} className={inputClass} value={form.note} onChange={set("note")}
                              placeholder="Créneau de livraison, installation souhaitée…"/>
                </div>
            </form>
        </Modal>,
        document.body
    );
};

const MarketplaceCartTab = ({cart, checkoutOpen, onCheckoutChange, onBrowse, onOrdered}) => {
    if (cart.lines.length === 0) {
        return (
            <ListEmpty
                icon={ShoppingCart}
                title="Votre panier est vide"
                text="Ajoutez du matériel depuis le catalogue pour passer commande."
                action={<Button variant="primary" className="mt-4" onClick={onBrowse}>Voir le catalogue</Button>}
            />
        );
    }

    // Edge-to-edge panes split by hairlines, like the Paramètres screen
    return (
        <div className="flex flex-col lg:flex-row lg:min-h-[calc(100vh-7.5rem)]">
            <section className="flex-1 min-w-0 flex flex-col">
                <div className={paneBarClass}>
                    <h2 className={paneTitleClass}>Panier · {cart.count} article(s)</h2>
                    <Button variant="ghost" size="sm" icon={Trash2} onClick={cart.clear}>Vider</Button>
                </div>
                {/* Desktop table */}
                <div className="hidden lg:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr>
                            <th className={thClass}>Produit</th>
                            <th className={`${thClass} text-right`}>Prix unitaire</th>
                            <th className={`${thClass} text-center`}>Quantité</th>
                            <th className={`${thClass} text-right`}>Total</th>
                            <th className={thClass}><span className="sr-only">Retirer</span></th>
                        </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {cart.lines.map(({product, quantity}) => (
                            <tr key={product.id}>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-4">
                                        <ProductThumb product={product}/>
                                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{product.name}</div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-right"><PriceCell product={product}/></td>
                                <td className="px-6 py-4 text-center">
                                    <QuantityStepper value={quantity} onChange={(q) => cart.setQuantity(product.id, q)}/>
                                </td>
                                <td className="px-6 py-4 text-right text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                    {formatMoney(effectivePrice(product) * quantity)}
                                </td>
                                <td className="px-6 py-4 text-right">
                                    <Button variant="danger" size="sm" icon={Trash2} title="Retirer" onClick={() => cart.remove(product.id)}/>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile rows */}
                <div className={mobileListClass}>
                    {cart.lines.map(({product, quantity}) => (
                        <div key={product.id} className={mobileRowClass}>
                            <div className="flex items-start gap-3">
                                <ProductThumb product={product}/>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{product.name}</div>
                                    <PriceCell product={product}/>
                                </div>
                                <Button variant="danger" size="sm" icon={Trash2} title="Retirer" onClick={() => cart.remove(product.id)}/>
                            </div>
                            <div className="mt-3 flex items-center justify-between">
                                <QuantityStepper value={quantity} onChange={(q) => cart.setQuantity(product.id, q)}/>
                                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                                    {formatMoney(effectivePrice(product) * quantity)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="mt-auto px-2 sm:px-4 py-2 border-t border-gray-200 dark:border-gray-700">
                    <Button variant="ghost" size="sm" icon={ArrowLeft} onClick={onBrowse}>
                        Continuer mes achats
                    </Button>
                </div>
            </section>

            <aside className="lg:w-[380px] flex-shrink-0 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
                <div className={paneBarClass}>
                    <h2 className={paneTitleClass}>Récapitulatif</h2>
                </div>
                <div className="px-4 sm:px-6 py-5 space-y-5">
                    <Totals cart={cart}/>
                    <Button variant="primary" icon={ShoppingBag} fullWidth onClick={() => onCheckoutChange(true)}>
                        Commander · {formatMoney(cart.total)}
                    </Button>
                    <ul className="space-y-2 text-xs text-gray-500 dark:text-gray-400">
                        <li className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary"/> Garantie constructeur sur le matériel</li>
                        <li className="flex items-center gap-2"><Headphones className="w-4 h-4 text-primary"/> L'équipe QuickCom vous recontacte pour confirmer</li>
                    </ul>
                </div>
            </aside>

            {checkoutOpen && (
                <CheckoutModal
                    cart={cart}
                    onClose={() => onCheckoutChange(false)}
                    onOrdered={() => {
                        cart.clear();
                        onCheckoutChange(false);
                        onOrdered?.();
                    }}
                />
            )}
        </div>
    );
};

export default MarketplaceCartTab;
